// Phase 8 of the weekly billing module — lab-facing weekly invoice list.
//
// Mounted as a tab inside LabPortalDashboard. Lists every weekly_lab_invoices
// row for the signed-in lab with download links to the rendered PDFs.
//
// Read-only on this side: the lab cannot mark anything paid from here.
// Admin owns status flow; this tab is for visibility + downloading the
// invoice PDF and confirming amounts.

import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Flex, Icon, Button, Badge, Spinner,
  Alert, AlertIcon, Stat, StatLabel, StatNumber, StatHelpText, SimpleGrid,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, useToast,
} from '@chakra-ui/react';
import { FaFilePdf, FaCalendarWeek, FaFileInvoiceDollar } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

const PDF_BUCKET = 'billing-pdfs';

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}

const STATUS_COLORS = { draft: 'gray', sent: 'blue', paid: 'green', void: 'red' };

export default function LabWeeklyInvoicesTab({ labId }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (!labId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('weekly_lab_invoices')
        .select('id, week_ending, status, subtotal_draws, admin_fee_total, ship_fee_total, total, sent_at, paid_at, paid_via, paid_reference, pdf_url, notes, invoice_number, generated_at')
        .eq('lab_id', labId)
        // The lab only ever needs to see sent / paid / void — drafts are
        // admin-internal until they hit Send.
        .neq('status', 'draft')
        .order('week_ending', { ascending: false });
      if (cancelled) return;
      if (error) {
        console.warn('Could not load weekly invoices:', error);
        setInvoices([]);
      } else {
        setInvoices(data || []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [labId]);

  const openPdf = async (path) => {
    if (!path) {
      toast({ title: 'PDF not available yet', status: 'warning', duration: 3000 });
      return;
    }
    const { data, error } = await supabase.storage.from(PDF_BUCKET).createSignedUrl(path, 60 * 60);
    if (error || !data?.signedUrl) {
      toast({ title: 'Could not open PDF', description: error?.message, status: 'error', duration: 4000 });
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  // Top-of-page stats — outstanding vs paid.
  const stats = (() => {
    const visible    = invoices.filter((i) => i.status !== 'void');
    const paid       = visible.filter((i) => i.status === 'paid');
    const sent       = visible.filter((i) => i.status === 'sent');
    return {
      outstanding: sent.reduce((s, i) => s + Number(i.total || 0), 0),
      paidTotal:   paid.reduce((s, i) => s + Number(i.total || 0), 0),
      count:       visible.length,
    };
  })();

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
        <HStack>
          <Icon as={FaFileInvoiceDollar} color="purple.500" />
          <Heading size="md">Weekly Invoices</Heading>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          Generated by AtHomeBloodDraw every Friday for the prior Mon–Sun.
        </Text>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={5}>
        <Stat bg="white" p={4} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Outstanding</StatLabel>
          <StatNumber color="orange.600">{fmtMoney(stats.outstanding)}</StatNumber>
          <StatHelpText fontSize="xs">awaiting your payment</StatHelpText>
        </Stat>
        <Stat bg="white" p={4} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Paid lifetime</StatLabel>
          <StatNumber color="green.600">{fmtMoney(stats.paidTotal)}</StatNumber>
          <StatHelpText fontSize="xs">closed invoices</StatHelpText>
        </Stat>
        <Stat bg="white" p={4} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Invoice count</StatLabel>
          <StatNumber>{stats.count}</StatNumber>
          <StatHelpText fontSize="xs">excluding voided</StatHelpText>
        </Stat>
      </SimpleGrid>

      {loading ? (
        <Flex justify="center" py={20}><Spinner size="lg" /></Flex>
      ) : invoices.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          No weekly invoices yet. They'll appear here once AtHomeBloodDraw sends the first one for your account.
        </Alert>
      ) : (
        <Box bg="white" borderRadius="md" borderWidth="1px" overflow="hidden">
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th><HStack spacing={1}><Icon as={FaCalendarWeek} /><Text>Week ending</Text></HStack></Th>
                  <Th>Status</Th>
                  <Th isNumeric>Subtotal</Th>
                  <Th isNumeric>Admin</Th>
                  <Th isNumeric>Shipping</Th>
                  <Th isNumeric>Total</Th>
                  <Th>Sent</Th>
                  <Th>Paid</Th>
                  <Th>Method</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {invoices.map((i) => (
                  <Tr key={i.id} opacity={i.status === 'void' ? 0.5 : 1}>
                    <Td fontWeight="medium">{fmtDate(i.week_ending)}</Td>
                    <Td><Badge colorScheme={STATUS_COLORS[i.status] || 'gray'}>{i.status}</Badge></Td>
                    <Td isNumeric>{fmtMoney(i.subtotal_draws)}</Td>
                    <Td isNumeric>{fmtMoney(i.admin_fee_total)}</Td>
                    <Td isNumeric>{Number(i.ship_fee_total || 0) === 0 ? '—' : fmtMoney(i.ship_fee_total)}</Td>
                    <Td isNumeric fontWeight="bold" color="green.600">{fmtMoney(i.total)}</Td>
                    <Td fontSize="xs" color="gray.600">{fmtDate(i.sent_at)}</Td>
                    <Td fontSize="xs" color="gray.600">{fmtDate(i.paid_at)}</Td>
                    <Td fontSize="xs" color="gray.600">{i.paid_via || '—'}</Td>
                    <Td>
                      {i.pdf_url && (
                        <Button size="xs" leftIcon={<FaFilePdf />} variant="outline" colorScheme="green" onClick={() => openPdf(i.pdf_url)}>
                          PDF
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
